import React, { useState, useEffect, useCallback } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import { MdOutlineRefresh } from 'react-icons/md';
import {
    LineChart,
    AreaChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { axiosInstance, convertFilterFormat } from '@appblocks/frontend/client/utils';
import TimeRangePicker from '@appblocks/frontend/cloudclient/Components/TimeRangePicker';
import DataPaginationRenderer from './DataPaginationRenderer';


const moment = require('moment');

export default function MetricsView(props) {
    const deviceId = props.deviceId;
    const [currentRange, setCurrentRange] = useState(60);
    const [currentMode, setCurrentMode] = useState('chart');
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [dataSources, setDataSources] = useState({});
    const [selectedDataTypes, setSelectedDataTypes] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [showMetricsTable, setShowMetricsTable] = useState(undefined);
    const [total, setTotal] = useState(0);
    const [metricsTableData, setMetricsTableData] = useState([]);

    const getMetrics = async ({
        search, start, count, startTime, endTime, filters,
    }) => {
        const parsedFilters = convertFilterFormat(filters);
        const res = await axiosInstance.get(`${process.env.API_URL}/cgg/devices/${deviceId ? `${deviceId}/` : ''}metrics`, {
            params: {
                search,
                start,
                count: 10000,
                startTime,
                endTime,
                ...parsedFilters,
            },
        });
        setMetricsTableData(res.data.data);
        setTotal(res.data.count);

        const tmpData = res.data.data.sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        );
        const tmpChartData = [];
        const tmpDataSources = {};
        for (let i = 0; i < tmpData.length; i++) {
            const row = tmpData[i];
            const entry = {
                createdAt: new Date(row.createdAt).toLocaleString(),
                [row.name]: Number(row.value),
            };
            const d1 = moment(row.createdAt);
            for (let j = i + 1; j < tmpData.length; j++) {
                const d2 = moment(tmpData[j].createdAt);
                if (d1.diff(d2, 'seconds') <= 0) {
                    entry[tmpData[j].name] = Number(tmpData[j].value);
                    i = j;
                    break;
                }
            }

            if (!tmpDataSources[row.name]) {
                tmpDataSources[row.name] = {
                    name: row.name,
                    value: row.value,
                    min: row.value,
                    max: row.value,
                    average: row.value,
                    lastUpdated: row.createdAt,
                    data: [],
                };
            }
            tmpDataSources[row.name].data.push(row.value);
            tmpDataSources[row.name].value = row.value;
            tmpChartData.push(entry);
        }
        setData(tmpData);
        setChartData(tmpChartData);
        setDataSources(tmpDataSources);
        setLoading(false);
        if (tmpData.length === 0) {
            setSelectedDataTypes([]);
        }
    };

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#9eeaf9', '#f99ebf'];
    const dataSourceKeys = Object.keys(dataSources).sort();

    return (
        <div>
            {loading && (
                <Spinner />
            )}
            {selectedDataTypes.length === 0 ? (
                <div className="alert alert-info">
                    Please select at least one metric to display.
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData}>
                        <defs>
                            {dataSourceKeys.map((dataType, index) => (
                                <linearGradient key={dataType} id={`color${dataType}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLORS[index]} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={COLORS[index]} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>
                        <XAxis dataKey="createdAt" />
                        <YAxis />
                        <Tooltip />
                        <CartesianGrid stroke="#eee" strokeDasharray="5 5" />

                        {selectedDataTypes.map((dataType, index) => (
                            <Area key={dataType} type="monotone" dataKey={dataType} stroke={COLORS[dataSourceKeys.indexOf(dataType)]} fillOpacity={1} fill={`url(#color${dataType})`} />
                        ))}
                    </AreaChart>

                    {/* <LineChart data={chartData} allowDuplicatedCategory={false}>
                        <XAxis dataKey="createdAt" />
                        <YAxis />
                        <Tooltip />
                        <CartesianGrid stroke="#eee" strokeDasharray="5 5" />
                        {selectedDataTypes.map((dataType, index) => (
                            <Line key={dataType} type="monotone" dataKey={dataType} />
                        ))}
                    </LineChart> */}
                </ResponsiveContainer>
            )
            }
            <DataPaginationRenderer
                total={total}
                perPage={10000}
                getItems={getMetrics}
                timeRange
            >
                <div className="table-responsive table-rounded shadow-sm mb-2">
                    <Table>
                        <thead className="table-secondary">
                            <tr>
                                <td />
                                <td>Name</td>
                                <td>Value</td>
                                <td>Last Updated</td>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(dataSources).map((key, index) => {
                                const dataSource = dataSources[key];
                                return (
                                    <tr key={index} className="align-middle">
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={
                                                    selectedDataTypes.includes(dataSource.name)
                                                }
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedDataTypes([
                                                            ...selectedDataTypes,
                                                            dataSource.name]);
                                                    } else {
                                                        setSelectedDataTypes(
                                                            selectedDataTypes
                                                                .filter(item => item !== dataSource
                                                                    .name),
                                                        );
                                                    }
                                                }}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                className="table-actions"
                                                type="button"
                                                onClick={() => setShowMetricsTable(dataSource.name)}
                                            >
                                                {dataSource.name}
                                            </button>
                                        </td>
                                        <td>{dataSource.value}</td>
                                        <td>{new Date(dataSource.lastUpdated).toLocaleString()}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </DataPaginationRenderer>
            <Modal
                show={showMetricsTable !== undefined}
                onHide={() => setShowMetricsTable(undefined)}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <span>{showMetricsTable}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <MetricsTable
                        deviceId={deviceId}
                        metricType={showMetricsTable}
                    />

                </Modal.Body>
            </Modal>
        </div>
    );
}

const MetricsTable = (props) => {
    const deviceId = props.deviceId;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [dataSources, setDataSources] = useState({});
    const [selectedDataTypes, setSelectedDataTypes] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [showMetricsTable, setShowMetricsTable] = useState(undefined);
    const [total, setTotal] = useState(0);
    const [metricsTableData, setMetricsTableData] = useState([]);
    const perPage = 10;

    const getMetrics = async ({
        search, start, count, startTime, endTime, filters,
    }) => {
        const parsedFilters = convertFilterFormat({
            ...filters,
            name: props.metricType,
        });
        const res = await axiosInstance.get(`${process.env.API_URL}/cgg/devices/${deviceId ? `${deviceId}/` : ''}metrics`, {
            params: {
                search,
                start,
                count: perPage,
                startTime,
                endTime,
                ...parsedFilters,
            },
        });
        setMetricsTableData(res.data.data);
        setTotal(res.data.count);
    };

    return (
        <DataPaginationRenderer
            total={total}
            timeRange
            search={false}
            perPage={perPage}
            getItems={getMetrics}
        >
            <Table>
                <thead className="table-secondary">
                    <tr>
                        <th>Time</th>
                        <th>Value</th>
                    </tr>
                </thead>
                <tbody>
                    {metricsTableData.map((row, index) => (
                        <tr key={index}>
                            <td>{new Date(row.createdAt).toLocaleString()}</td>
                            <td>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </DataPaginationRenderer>
    );
};
